import React, { useState, useEffect } from 'react';
import { motion, animate } from 'framer-motion';
import { CheckCircle, Wind, Box, Leaf, Users, Plus, Loader2 } from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import PageTitleBar from '../components/common/PageTitleBar/PageTitleBar';
import './Dashboard.css';
import './Services.css';
import './styles/pages-common.css';
import { usePageLoader } from '../components/common/PageLoader/PageLoader';
import Map from "./Map";
import footerImgNew from '../assets/footer-img.png';

const AnimatedCounter = ({ value, duration = 1.5, toLocale = true }) => {
  const [count, setCount] = useState(0);
  const [parts, setParts] = useState({ prefix: '', suffix: '' });

  useEffect(() => {
    if (value == null) return;

    // Parse prefix, numeric value, and suffix
    const match = String(value).match(/^([~]?)([0-9,.]+)(.*)$/);
    if (!match) {
      setCount(value);
      setParts({ prefix: '', suffix: '' });
      return;
    }

    const prefix = match[1];
    const numValue = parseFloat(match[2].replace(/,/g, ''));
    const suffix = match[3];
    setParts({ prefix, suffix });

    if (isNaN(numValue)) {
      setCount(value);
      return;
    }

    const controls = animate(0, numValue, {
      duration: duration,
      ease: 'easeOut',
      onUpdate: (latest) => {
        setCount(latest);
      },
    });

    return () => controls.stop();
  }, [value, duration]);

  if (typeof count === 'number') {
    const hasDecimals = typeof value === 'number' && value % 1 !== 0;
    let formattedNum = '';
    if (hasDecimals) {
      formattedNum = toLocale ? count.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : count.toFixed(2);
    } else {
      formattedNum = toLocale ? Math.round(count).toLocaleString() : Math.round(count).toString();
    }
    return `${parts.prefix}${formattedNum}${parts.suffix}`;
  }

  return count;
};

export const Dashboard = ({ setView }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [dashboardSummaryData, setDashboardSummaryData] = useState(() => {
    try {
      const stored = localStorage.getItem('dashboardSummary');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [summaryLoading, setSummaryLoading] = useState(() => {
    try {
      const stored = localStorage.getItem('dashboardSummary');
      return stored ? false : true;
    } catch {
      return true;
    }
  });
  const [alert, setAlert] = useState(null);

  const { setPageLoading } = usePageLoader();

  useEffect(() => {
    setPageLoading(summaryLoading);
    return () => setPageLoading(false);
  }, [summaryLoading, setPageLoading]);

  // Function to fetch user details from API
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        console.warn('No valid session found');
        return null;
      }

      // Fetch user details from API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();

      if (data.status && data.data?.user) {
        const userData = data.data.user;

        // Normalize profile picture field - ensure 'picture' is set
        if (userData.profile_picture && !userData.picture) {
          userData.picture = userData.profile_picture;
        }

        setUser(userData);
        // Also store in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } else {
        throw new Error('Invalid user data response');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // If API fails, try to use stored user data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        return userData;
      }
      return null;
    }
  };


  const fetchDashboardSummary = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        console.warn('No valid session found');
        return null;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/dashboard-summary/`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard summary');
      }

      const result = await response.json();

      if (result.success && result.data) {
        setDashboardSummaryData(result.data);
        localStorage.setItem('dashboardSummary', JSON.stringify(result.data));
        return result.data;
      } else {
        throw new Error('Invalid dashboard summary response');
      }
    } catch (error) {
      console.error('Error loading dashboard summary:', error);
      const storedSummary = localStorage.getItem('dashboardSummary');
      if (storedSummary) {
        const summaryData = JSON.parse(storedSummary);
        setDashboardSummaryData(summaryData);
        return summaryData;
      }
      return null;
    }
  };


  // Load user and dashboard data on component mount (progressively and decoupled)
  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async () => {
      // 1. Trigger API calls in parallel
      const userPromise = fetchUserDetails();
      const summaryPromise = fetchDashboardSummary();

      // 2. Background user session validation (non-blocking)
      userPromise.then((userData) => {
        if (!userData && isMounted) {
          setAlert({
            type: 'error',
            message: 'Error loading user information'
          });
          setTimeout(() => {
            setView('signin');
          }, 500);
        }
      });

      // 3. Resolve summary, updating loading state
      summaryPromise.finally(() => {
        if (isMounted) setSummaryLoading(false);
      }).catch(err => {
        console.error('Error loading dashboard summary:', err);
        if (isMounted) {
          setSummaryLoading(false);
        }
      });
    };

    loadDashboardData();

    // 3-minute auto-refresh interval for live summary data updates
    const intervalId = setInterval(() => {
      if (isMounted) {
        fetchDashboardSummary();
      }
    }, 3 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [setView]);

  const formatStatValue = (val, rawUnit, fallbackVal, fallbackUnit) => {
    if (val == null) {
      return (
        <>
          <AnimatedCounter value={fallbackVal} />
          <span style={{ fontSize: '13px', fontWeight: '400', color: '#94a3b8', marginLeft: '4px', textTransform: 'none' }}>{fallbackUnit}</span>
        </>
      );
    }
    return (
      <>
        <AnimatedCounter value={val} />
        <span style={{ fontSize: '13px', fontWeight: '400', color: '#94a3b8', marginLeft: '4px', textTransform: 'none' }}> {rawUnit}</span>
      </>
    );
  };

  // Show exact API data without calculations
  const peopleSupportedText = dashboardSummaryData?.people_supported_right_now != null
    ? `~${Math.round(dashboardSummaryData.people_supported_right_now).toLocaleString()}`
    : 'No Data';

  // const airPerMinL = formatStatValue(dashboardSummaryData?.estimated_cleaner_air_per_minute_cuft, 'cu ft', 'No Data', '');

  // const airPerHourM3 = formatStatValue(dashboardSummaryData?.estimated_cleaner_air_per_hour_cuft, 'cu ft', 'No Data', '');

  // const freshAirM3Text = formatStatValue(dashboardSummaryData?.fresh_air_generated_today_cuft, 'cu ft', 'No Data', '');

  // const totalAirM3Text = formatStatValue(dashboardSummaryData?.total_air_treated_today_cuft, 'cu ft', 'No Data', '');



  const airPerMinL = formatStatValue(dashboardSummaryData?.estimated_cleaner_air_per_minute_cuft, '', 'No Data', '');

  const airPerHourM3 = formatStatValue(dashboardSummaryData?.estimated_cleaner_air_per_hour_cuft, '', 'No Data', '');

  // Impact Overview exact values:
  const freshAirM3Text = formatStatValue(dashboardSummaryData?.fresh_air_generated_today_cuft, '', 'No Data', '');

  const totalAirM3Text = formatStatValue(dashboardSummaryData?.total_air_treated_today_cuft, '', 'No Data', '');



  // Cumulative people served formatting
  const peopleServedText = dashboardSummaryData?.cumulative_people_served != null
    ? dashboardSummaryData.cumulative_people_served.toLocaleString()
    : 'No Data';

  // Pole statistics calculations:
  const totalPoles = dashboardSummaryData?.total_poles != null ? dashboardSummaryData.total_poles : 'No Data';
  const onlinePoles = dashboardSummaryData?.online_poles != null ? dashboardSummaryData.online_poles : 'No Data';
  const offlinePoles = dashboardSummaryData?.offline_poles != null ? dashboardSummaryData.offline_poles : 'No Data';
  const noAnalyticsPoles = dashboardSummaryData?.no_analytics_poles != null ? dashboardSummaryData.no_analytics_poles : 'No Data';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="page-root"
    >
      {/* ── Shared Header ── */}
      <InnerHeader
        user={user}
        setView={setView}
        onProfilePhotoSuccess={async () => {
          setTimeout(async () => {
            await fetchUserDetails();
          }, 500);
          setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
          setTimeout(() => setAlert(null), 3000);
        }}
      />

      {/* Alert */}
      {alert && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`page-alert ${alert.type}`}
        >
          <CheckCircle size={18} />
          <span className="alert-message-flex">{alert.message}</span>
          <button
            onClick={() => setAlert(null)}
            className="page-alert-close"
          >
            ×
          </button>
        </motion.div>
      )}


      {/* Page Title */}
      <PageTitleBar title="Dashboard" setView={setView} activeBtn="dashboard" />

      {/* Summary & KPIs Section */}
      {/* <div className="dash-summary-section">
       
        <div className="dash-summary-header">
          <span className="dash-summary-header-label">Summary &amp; KPIs</span>
        </div>

      
        <div className="dash-kpi-grid">

         
          <div className="dash-kpi-cell w-400">
            <div className="dash-kpi-label">
              Average Reduction Achieved Today
            </div>
            <div className="dash-kpi-stat-row">

              <div className="dash-kpi-stat">
                <span className="dash-kpi-stat-value">{reductionData?.pm10 != null ? reductionData.pm10 : '00.0'}%</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="dash-kpi-stat-icon">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className='w-100'> <span className="dash-kpi-stat-sub-label">PM<sub>10</sub></span></div>
              </div>
              <div className="dash-kpi-stat">
                <span className="dash-kpi-stat-value">{reductionData?.pm2_5 != null ? reductionData.pm2_5 : '00.0'}%</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="dash-kpi-stat-icon">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg><br />
                <div className='w-100'><span className="dash-kpi-stat-sub-label">PM<sub>2.5</sub></span></div>
              </div>
              <div className="dash-kpi-stat">
                <span className="dash-kpi-stat-value">{reductionData?.co2 != null ? reductionData.co2 : '00'}%</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="dash-kpi-stat-icon">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className='w-100'><span className="dash-kpi-stat-sub-label">CO<sub>2</sub></span></div>
              </div>
            </div>
            <div className="dash-kpi-stat-sub">
              <span className="dash-kpi-stat-sub-label">PM₂.₅</span>
              <span className="dash-kpi-stat-sub-label">PM₁₀</span>
              <span className="dash-kpi-stat-sub-label">CO₂</span>
            </div>
          </div>

        
          <div className="dash-kpi-cell">
            <div className="dash-kpi-label">
              Total Active Poles
            </div>
            <div className="dash-kpi-big-value">{dashboardData?.online_poles}</div>
            <div className="dash-kpi-sub-text">
              <span className="dash-kpi-online"> Online</span><br />
              <span className="dash-kpi-offline">{dashboardData?.offline_poles} Offline</span> 
              <span className="dash-kpi-divider">/</span>
              <span>{dashboardData?.offline_poles} Offline</span> 
            </div>
          </div>

         
          <div className="dash-kpi-cell">
            <div className="dash-kpi-label">
              AirPulse™ Score:

            </div>
            <div className="dash-kpi-big-value-orange">{reductionData?.airpulse ?? 0}</div>
            <div className="dash-aqi-wrap">
              <div className="dash-aqi-bar">
                <div className="dash-aqi-indicator" style={{ left: `${reductionData?.airpulse ?? 0}%` }} />
              </div>
              <div className="dash-aqi-labels">
                <span className="dash-aqi-label-low">Low</span>
                <span className="dash-aqi-label-mid">Unhealthy</span>
                <span className="dash-aqi-label-high">High</span>
              </div>
            </div>
          </div>

         
          <div className="dash-kpi-cell">
            <div className="dash-kpi-label">
              Telemetry Sync As Of
            </div>
            <div className="dash-kpi-big-value">{currentTime || '...'}</div>
            <div className="dash-sync-date">{currentDate || '...'}</div>
          </div>
        </div>
      </div> */}

      {/* <div className="dash-summary-section">
        <div className="dash-kpi-cell tele-sync-wrap">
          <span className="dash-kpi-label">
            Telemetry Sync As Of

          </span>
          <span className="dash-kpi-big-value">{loginTime || '...'}</span>
          <span className="dash-sync-date">{loginDate || '...'}</span>
        </div>
      </div> */}
      <div className="dashboard-new">
        {/* ── Human Breathing Benefit Card (Left) ── */}
        <div className="dash-new-left-card">
          <div className="breathing-benefit-content">
            <div className="breathing-benefit-header">
              <h3>HUMAN BREATHING BENEFIT</h3>
              <p>Cleaner air delivered. Measurable impact. Better lives.</p>
            </div>

            <div className="breathing-benefit-body">
              {/* Left Column: Silhouette & Ring graphic */}
              <div className="benefit-graphic-panel">
                <div className="benefit-graphic-overlay"></div>
                <div className="benefit-graphic-text">
                  <span className="benefit-number">
                    {summaryLoading ? (
                      <span className="value-skeleton" style={{ width: '120px', height: '48px', display: 'inline-block' }} />
                    ) : (
                      <AnimatedCounter value={peopleSupportedText} />
                    )}
                  </span>
                  <span className="benefit-label-1">People Supported</span>
                  <span className="benefit-label-2">Right Now</span>
                  <span className="benefit-label-3">Across the Network</span>
                </div>
              </div>

              {/* Right Column: Air per person stats */}
              <div className="benefit-stats-panel">
                <h4>Cleaner Air per Person</h4>

                <div className="benefit-stats-row">
                  <div className="benefit-stat-item">
                    <div className="benefit-stat-value">
                      <Wind className="stat-icon orange-glow" size={18} />
                      {summaryLoading ? (
                        <span className="value-skeleton" style={{ width: '70px', height: '22px' }} />
                      ) : (
                        <span>{airPerMinL}</span>
                      )}
                    </div>
                    <span className="benefit-stat-sub">cu ft per minute</span>
                  </div>

                  <div className="benefit-stat-item">
                    <div className="benefit-stat-value">
                      <Box className="stat-icon orange-glow" size={18} />
                      {summaryLoading ? (
                        <span className="value-skeleton" style={{ width: '70px', height: '22px' }} />
                      ) : (
                        <span>{airPerHourM3}</span>
                      )}
                    </div>
                    <span className="benefit-stat-sub">cu ft per hour</span>
                  </div>
                </div>

                <div className="benefit-quote-box">
                  <p className="quote-accent">Breathe easier. Live better.</p>
                  <p className="quote-sub">Measured across our active filtration network.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column (Impact Overview + Poles Status Grid) ── */}
        <div className="dash-new-right-col">
          {/* Impact Overview Card */}
          <div className="impact-overview-card">
            <h3>IMPACT OVERVIEW</h3>

            <div className="impact-stats-list">
              <div className="impact-stat-row">
                <div className="impact-stat-label">
                  <Leaf className="impact-icon green" size={16} />
                  <span>Fresh Air Generated Today</span>
                </div>
                {summaryLoading ? (
                  <span className="value-skeleton" style={{ width: '80px', height: '20px' }} />
                ) : (
                  <span className="impact-stat-value">{freshAirM3Text}</span>
                )}
              </div>

              <div className="impact-stat-row">
                <div className="impact-stat-label">
                  <Wind className="impact-icon orange" size={16} />
                  <span>Total Air Treated Today</span>
                </div>
                {summaryLoading ? (
                  <span className="value-skeleton" style={{ width: '80px', height: '20px' }} />
                ) : (
                  <span className="impact-stat-value">{totalAirM3Text}</span>
                )}
              </div>

              <div className="impact-stat-row">
                <div className="impact-stat-label">
                  <Users className="impact-icon green" size={16} />
                  <span>Cumulative People Served</span>
                </div>
                {summaryLoading ? (
                  <span className="value-skeleton" style={{ width: '80px', height: '20px' }} />
                ) : (
                  <span className="impact-stat-value"><AnimatedCounter value={peopleServedText} /></span>
                )}
              </div>
            </div>
          </div>

          {/* Poles Status 2x2 Grid Card */}
          <div className="poles-status-card">
            <div className="poles-grid-container">
              {/* Total Poles */}
              <div className="poles-grid-item">
                <span className="poles-grid-label">Total Poles</span>
                <span className="poles-grid-number">
                  {summaryLoading ? (
                    <span className="value-skeleton" style={{ width: '50px', height: '24px' }} />
                  ) : (
                    <AnimatedCounter value={totalPoles} />
                  )}
                </span>
              </div>

              {/* Online Poles */}
              <div className="poles-grid-item">
                <span className="poles-grid-label">Online Poles</span>
                <span className="poles-grid-number text-online">
                  {summaryLoading ? (
                    <span className="value-skeleton" style={{ width: '50px', height: '24px' }} />
                  ) : (
                    <AnimatedCounter value={onlinePoles} />
                  )}
                </span>
              </div>

              {/* Offline Poles */}
              <div className="poles-grid-item">
                <span className="poles-grid-label">Offline Poles</span>
                <span className="poles-grid-number text-offline">
                  {summaryLoading ? (
                    <span className="value-skeleton" style={{ width: '50px', height: '24px' }} />
                  ) : (
                    <AnimatedCounter value={offlinePoles} />
                  )}
                </span>
              </div>

              {/* Awaiting Connection/No Analytics Poles */}
              <div className="poles-grid-item">
                <span className="poles-grid-label">Awaiting Connection</span>
                <span className="poles-grid-number text-maintenance">
                  {summaryLoading ? (
                    <span className="value-skeleton" style={{ width: '50px', height: '24px' }} />
                  ) : (
                    <AnimatedCounter value={noAnalyticsPoles} />
                  )}
                </span>
              </div>

              {/* Centered Divider Cross Icon */}
              <div className="grid-cross-divider">
                <Plus className="cross-icon" size={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="AtmosPOLE-Network-wrap">
        <h2>AtmosPOLE Network</h2>
        {/* <img src={networkMap} alt="Network Map" className='w-100' /> */}
        {/* <Map /> */}
        <Map />
      </div>
      <img src={footerImgNew} alt="" style={{ width: '100%', height: 'auto' }} />
      {/* ── Shared Footer ── */}
      <InnerFooter />
    </motion.div>
  );
};

export default Dashboard;
